import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, FileDown, Pencil, Trash2 } from "lucide-react";
import { api, downloadFile, errMsg } from "@/lib/api";
import Appeals from "@/pages/Appeals";
import { fmtDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { OutcomeBadge, StatusBadge } from "@/components/StatusBadge";
import { RiderFormDialog } from "@/components/RiderFormDialog";
import { Avatar, AvatarEdit } from "@/components/Avatar";
import { RiderDocs } from "@/components/RiderDocs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-1 break-words font-medium">{value || "—"}</div>
  </div>
);

export const AssessmentHistory = ({ items }) => (
  <section className="rounded-2xl border bg-card" data-testid="assessment-history">
    <h3 className="border-b px-5 py-4 text-base font-bold">Assessment history</h3>
    {items.length === 0 && <p className="px-5 py-8 text-sm text-muted-foreground">No assessments recorded in this system yet.</p>}
    {items.map((a) => (
      <Link key={a.id} to={`/assessments/${a.id}`} data-testid={`history-row-${a.id}`} className="flex items-center justify-between gap-3 border-b px-5 py-4 last:border-0 hover:bg-secondary/50">
        <div>
          <div className="font-semibold">{fmtDate(a.date)} · {a.assessment_type === "initial" ? "Initial assessment" : "Re-assessment"}</div>
          <div className="text-xs text-muted-foreground">Assessor: {a.assessor_name}</div>
        </div>
        <OutcomeBadge outcome={a.outcome} />
      </Link>
    ))}
  </section>
);

export default function RiderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [edit, setEdit] = useState(false);

  const load = () => api.get(`/riders/${id}`).then((res) => setR(res.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async () => {
    try {
      await api.delete(`/riders/${id}`);
      toast.success("Rider deleted");
      nav("/riders");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!r) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const canEdit = ["admin", "assessor"].includes(user.role);
  const pdf = () => downloadFile(`/reports/rider/${r.id}.pdf`, `SSCBB-rider-${r.last_name}-${r.first_name}.pdf`).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="space-y-8">
      <Link to="/riders" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Riders</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {canEdit ? <AvatarEdit photo={r.photo} name={`${r.first_name} ${r.last_name}`} endpoint={`/avatars/rider/${r.id}`} onSaved={(p) => setR({ ...r, photo: p })} testid="rider-avatar" /> : <Avatar photo={r.photo} name={`${r.first_name} ${r.last_name}`} size="lg" testid="rider-avatar" />}
          <div>
          <h1 data-testid="rider-name" className="text-3xl font-extrabold tracking-tight sm:text-4xl">{r.first_name} {r.last_name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <StatusBadge status={r.status} testId="rider-status" />
            {!r.active && <span className="text-xs text-muted-foreground">Inactive</span>}
          </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="rider-pdf-btn" onClick={pdf} className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold hover:bg-secondary"><FileDown className="h-4 w-4" /> PDF report</button>
        {canEdit && (
          <>
            <button data-testid="rider-assess-btn" onClick={() => nav(`/assess/new?rider=${r.id}`)} className="inline-flex items-center gap-2 rounded-full bg-destructive px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700"><ClipboardCheck className="h-4 w-4" /> Start check ride</button>
            <button data-testid="rider-edit-btn" onClick={() => setEdit(true)} className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold hover:bg-secondary"><Pencil className="h-4 w-4" /> Edit</button>
            {user.role === "admin" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="rider-delete-btn" className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-4 py-2.5 text-sm font-semibold text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this rider?</AlertDialogTitle>
                    <AlertDialogDescription>This permanently removes the rider, all their assessment records and their login.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="rider-delete-confirm" onClick={remove} className="bg-destructive text-white hover:bg-red-700">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </>
        )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <section className="space-y-6 rounded-2xl border bg-card p-6 lg:col-span-5">
          <div className="rounded-xl border-l-4 border-l-primary bg-secondary/50 p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Next re-assessment due</div>
            <div data-testid="rider-next-due" className="mt-1 font-mono text-2xl font-semibold">{fmtDate(r.next_due_date)}</div>
            {r.days_until_due != null && <div className="text-xs text-muted-foreground">{r.days_until_due < 0 ? `${-r.days_until_due} days overdue` : `${r.days_until_due} days remaining`}</div>}
          </div>
          <div className="grid grid-cols-2 gap-5">
            <Info label="Last passed" value={fmtDate(r.last_pass_date)} />
            <Info label="Member type" value={r.member_type === "new" ? "New member" : "Existing"} />
            <Info label="Qualification" value={r.qualification} />
            <Info label="Qualified" value={r.qualification_date && fmtDate(r.qualification_date)} />
            <Info label="Membership no." value={r.membership_number} />
            <Info label="Licence no." value={r.licence_number} />
            <Info label="Email" value={r.email} />
            <Info label="Phone" value={r.phone} />
          </div>
          {r.notes && <Info label="Notes" value={r.notes} />}
        </section>
        <div className="space-y-6 lg:col-span-7">
          <RiderDocs riderId={r.id} manage={(user.permissions?.riders || 0) >= 2} />
          <AssessmentHistory items={r.assessments} />
          {["admin", "trustee"].includes(user.role) && <Appeals riderId={r.id} embedded />}
        </div>
      </div>
      {edit && <RiderFormDialog open={edit} onOpenChange={setEdit} rider={r} onSaved={() => load()} />}
    </div>
  );
}
